// src/components/ReturnsList.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  query,
  orderBy,
  where,
  limit,
  startAfter,
  serverTimestamp,
  doc,
  updateDoc,
  addDoc,
  deleteDoc,
  Timestamp,
  getDoc,
  setDoc,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase/firebaseConfig";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { getFunctions, httpsCallable } from "firebase/functions";
import logoUrl from "../components/assets/logo.png";

const PAGE_SIZE = 25;
const ENTRY_ROUTE_CYL = "/returns/new";
const ENTRY_ROUTE_PARTS = "/returns/parts/new";
const LOGO_W_PT = 64;

const PDF_MAX_IMAGES = 12;
const PDF_IMG_COLS = 3;
const PDF_IMG_SIZE = 150;
const PDF_IMG_GAP = 10;
const PDF_LEFT_MARGIN = 40;

const VIEW = {
  CYL: "cylinders",
  PARTS: "parts",
};

export default function ReturnsList() {
  const navigate = useNavigate();

  const [view, setView] = useState(VIEW.CYL); // cylinders | parts
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [error, setError] = useState("");

  // filters
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [qtext, setQtext] = useState("");
  const [rnSearch, setRnSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // pagination
  const [pageIndex, setPageIndex] = useState(0);
  const [lastSnap, setLastSnap] = useState(null);

  // preload logo to an <img> so jsPDF can consume it
  const [logoImg, setLogoImg] = useState(null);
  useEffect(() => {
    if (!logoUrl) return;
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = logoUrl;
    img.onload = () => setLogoImg(img);
  }, []);

  const qtextLower = qtext.trim().toLowerCase();
  const rnSearchTrim = rnSearch.trim();

  /* -------------------- data fetch -------------------- */

  const baseCol = view === VIEW.CYL ? "returns" : "returns_parts";

  const fetchPage = async ({ reset } = { reset: true }) => {
    setLoading(true);
    setError("");

    try {
      const col = collection(db, baseCol);
      const parts = [orderBy("createdAt", "desc")];

      if (fromDate) {
        parts.push(
          where(
            "createdAt",
            ">=",
            Timestamp.fromDate(new Date(fromDate + "T00:00:00"))
          )
        );
      }
      if (toDate) {
        parts.push(
          where(
            "createdAt",
            "<=",
            Timestamp.fromDate(new Date(toDate + "T23:59:59"))
          )
        );
      }
      if (!reset && lastSnap) parts.push(startAfter(lastSnap));
      parts.push(limit(PAGE_SIZE));

      const qy = query(col, ...parts);
      const snap = await getDocs(qy);
      const docs = snap.docs.map((d) => ({ id: d.id, _snap: d, ...d.data() }));

      setRows(docs);
      setLastSnap(snap.docs[snap.docs.length - 1] || null);
      setPageIndex((i) => (reset ? 0 : i + 1));
    } catch (e) {
      console.error(e);
      setError(
        "Failed to load returns. If you just added date filters, Firestore may need a composite index for (createdAt, orderBy)."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // whenever view changes, refresh
    setLastSnap(null);
    fetchPage({ reset: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const applyFilters = async () => {
    setLastSnap(null);
    await fetchPage({ reset: true });
  };

  const nextPage = async () => {
    if (rows.length === 0 || !lastSnap) return;
    await fetchPage({ reset: false });
  };

  const prevPage = async () => {
    // simple reset to first (keeps code short)
    setLastSnap(null);
    await fetchPage({ reset: true });
  };


/* -------------------- client-side search -------------------- */

const filtered = useMemo(() => {
  return rows.filter((r) => {
    const f = r.payload?.form || {};
    const rowStatus = String(r.status || "new");

    // --- status filter ---
    if (statusFilter !== "all" && rowStatus !== statusFilter) {
      return false;
    }


    // --- generic text search (existing behaviour) ---
    if (qtextLower) {
      const hay =
        [
          f.serialNumber,
          f.sretNumber,
          f.returnsNoteNumber,
          f.cylinderModel,
          f.reasonCode,
          f.reportedCode,
          f.faultFoundCode,
          r.id,
          f.partCode,
          f.partTitle,
        ]
          .filter(Boolean)
          .map((x) => String(x).toLowerCase()) || [];

      const matchesGeneric = hay.some((x) => x.includes(qtextLower));
      if (!matchesGeneric) return false;
    }

    // --- dedicated Returns Note search ---
    if (rnSearchTrim) {
      const rn = String(f.returnsNoteNumber || "").toLowerCase();
      const digits = rn.replace(/\D+/g, ""); // strip RN- and zeros etc

      const rnNeedle = rnSearchTrim.toLowerCase();

      const matchesRn =
        rn.includes(rnNeedle) || // matches anywhere in full RN string
        digits.endsWith(rnSearchTrim); // or matches the tail of numeric part

      if (!matchesRn) return false;
    }

    return true;
  });
}, [rows, qtextLower, rnSearchTrim, statusFilter]);


  /* -------------------- admin actions -------------------- */

  // write small admin status/notes (kept from your original)
  const saveAdmin = async (rowId, { adminNotes, status }) => {
    const updates = {
      status,
      statusUpdatedAt: serverTimestamp(),
      adminNotes: adminNotes ?? "",
      notesUpdatedAt:
        adminNotes && adminNotes.trim() ? serverTimestamp() : null,
    };
    await updateDoc(doc(db, baseCol, rowId), updates);
    setRows((prev) => prev.map((r) => (r.id === rowId ? { ...r, ...updates } : r)));
  };

  // Demote: create/update draft, then DELETE the submitted doc
  const demoteToDraft = async (r, openAfter = true) => {
    const payload = r.payload || {};
    const draftRefId = r.draftRef || null;
    const draftCollection =
      view === VIEW.CYL ? "returns_draft" : "returns_parts_draft";
    const entryRoute = view === VIEW.CYL ? ENTRY_ROUTE_CYL : ENTRY_ROUTE_PARTS;

    if (
      !window.confirm(
        "Demote this submitted report back to Draft? The submitted copy will be removed from the submitted list."
      )
    )
      return;

    try {
      let targetDraftId = draftRefId;
      if (targetDraftId) {
        await updateDoc(doc(db, draftCollection, targetDraftId), {
          ...payload,
          status: "in_progress",
          updatedAt: serverTimestamp(),
          submittedAt: null,
          finalRefId: r.id,
        });
      } else {
        const res = await addDoc(collection(db, draftCollection), {
          ...payload,
          status: "in_progress",
          progress: estimateProgress(view, payload),
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          finalRefId: r.id,
        });
        targetDraftId = res.id;
      }

      // delete the original submitted doc so there is no clone
      await deleteDoc(doc(db, baseCol, r.id));

      // refresh UI
      setRows((prev) => prev.filter((x) => x.id !== r.id));

      if (openAfter && targetDraftId) {
        navigate({ pathname: entryRoute, search: `?draft=${targetDraftId}` });
      }
    } catch (e) {
      console.error(e);
      alert("Could not demote to draft.");
    }
  };

  // Archive: copy to *_archive, force status: complete, then delete original
  const archiveReport = async (r) => {
    const archiveCol =
      view === VIEW.CYL ? "returns_archive" : "returns_parts_archive";

    if (
      !window.confirm(
        "Archive this report? It will be marked complete, moved to Archive, and removed from the submitted list."
      )
    )
      return;

    try {
      const snapshot = { ...r };
      // Ensure we don’t drag internal fields through
      delete snapshot._snap;

      const archDoc = {
        ...snapshot,
        status: "complete",
        statusUpdatedAt: serverTimestamp(),
        archivedAt: serverTimestamp(),
      };

      await setDoc(doc(collection(db, archiveCol)), archDoc);
      await deleteDoc(doc(db, baseCol, r.id));
      setRows((prev) => prev.filter((x) => x.id !== r.id));
    } catch (e) {
      console.error(e);
      alert("Could not archive this report.");
    }
  };

  /* -------------------- render -------------------- */

        return (
          <div className="max-w-7xl mx-auto px-4 py-6">
            {/* header + filters */}
            {/* header + filters */}
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
        <div className="flex items-center gap-3">
          <h1 className="text-xl sm:text-2xl font-bold">Submitted Returns</h1>

          <div className="inline-flex rounded-md shadow-sm" role="group">
            <button
              className={`px-3 py-1.5 border text-sm rounded-l-md ${
                view === VIEW.CYL
                  ? "bg-indigo-600 text-white border-indigo-600"
                  : "bg-white border-gray-300 hover:bg-gray-50"
              }`}
              onClick={() => setView(VIEW.CYL)}
            >
              Cylinders
            </button>
            <button
              className={`px-3 py-1.5 border text-sm rounded-r-md ${
                view === VIEW.PARTS
                  ? "bg-indigo-600 text-white border-indigo-600"
                  : "bg-white border-gray-300 hover:bg-gray-50"
              }`}
              onClick={() => setView(VIEW.PARTS)}
            >
              Parts
            </button>
          </div>
        </div>

        {/* filters */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:flex lg:items-center gap-2 sm:ml-auto">
          <div className="flex items-center gap-2">
            <input
              type="date"
              className="border rounded px-2 py-1 text-sm w-full"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              title="From date"
            />
            <span className="hidden sm:inline text-gray-500 text-sm">to</span>
            <input
              type="date"
              className="border rounded px-2 py-1 text-sm w-full"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              title="To date"
            />
          </div>

          <button
            className="px-3 py-1.5 rounded-md border border-gray-300 hover:bg-gray-50 text-sm"
            onClick={applyFilters}
            disabled={loading}
          >
            Apply
          </button>

          <input
            className="border rounded px-2 py-1 text-sm w-full sm:w-64"
            placeholder="Search serial / SRET / note / model / code / id"
            value={qtext}
            onChange={(e) => setQtext(e.target.value)}
          />

          <input
            className="border rounded px-2 py-1 text-sm w-full sm:w-40"
            placeholder="Search RN# (e.g. 12)"
            value={rnSearch}
            onChange={(e) => setRnSearch(e.target.value)}
            title="Search by Returns Note number"
          />

          <select
            className="border rounded px-2 py-1 text-sm w-full sm:w-48"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            title="Filter by status"
          >
            <option value="all">All statuses</option>
            <option value="new">New</option>
            <option value="waiting_collection">Waiting for collection</option>
            <option value="waiting_processing">Waiting to be processed</option>
            <option value="in_progress">In progress</option>
            <option value="complete">Complete</option>
            <option value="straggler">Straggler</option>
          </select>


        </div>
      </div>


      {error && <div className="mb-3 text-red-600 text-sm">{error}</div>}

      {/* list */}
      <div className="space-y-3">
        {loading && rows.length === 0 && (
          <div className="text-gray-500">Loading…</div>
        )}
        {!loading && rows.length === 0 && (
          <div className="text-gray-500">No returns found.</div>
        )}

        {filtered.map((r) => {
          const f = r.payload?.form || {};
          const dt = r.createdAt?.toDate?.();
          const isComplete = String(r.status || "").toLowerCase() === "complete";
          return (
            <div key={r.id} className="border rounded-lg bg-white">
              <div key={r.id} className="border rounded-lg bg-white">
                <div className="p-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
                  {/* LEFT SIDE: type, title, RN/Serial/SRET, status */}
                  <div className="flex-1 min-w-0">
                    {/* Top row: type pill, title, status */}
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-xs rounded px-2 py-0.5 border bg-gray-50">
                        {view === VIEW.CYL ? "Cylinder" : "Part"}
                      </span>

                      <span className="font-medium truncate max-w-xs sm:max-w-sm">
                        {view === VIEW.CYL
                          ? f.cylinderModel || "—"
                          : f.partCode || f.partTitle || "—"}
                      </span>

                      {(() => {
                        const s = r.status || "new";
                        return (
                          <span
                            className={
                              "text-xs rounded px-2 py-0.5 border " +
                              statusClasses(s)
                            }
                          >
                            Status: {statusLabel(s)}
                          </span>
                        );
                      })()}
                    </div>

                    {/* Second row: RN / Serial / SRET in smaller, neat line */}
                    <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs sm:text-sm text-gray-600">
                      <span>
                        Returns Note:{" "}
                        <span className="font-mono">{f.returnsNoteNumber || "—"}</span>
                      </span>
                      <span>
                        Serial:{" "}
                        <span className="font-mono">{f.serialNumber || "—"}</span>
                      </span>
                      <span>
                        SRET #:{" "}
                        <span className="font-mono">{f.sretNumber || "—"}</span>
                      </span>
                    </div>
                  </div>

                  {/* RIGHT SIDE: timestamp + actions */}
                  <div className="flex flex-wrap items-center justify-end gap-2">
                    <div className="text-xs sm:text-sm text-gray-500">
                      {dt ? dt.toLocaleString() : "—"}
                    </div>

                    <button
                      className="text-indigo-600 hover:underline text-xs sm:text-sm"
                      onClick={() => setOpenId((p) => (p === r.id ? null : r.id))}
                    >
                      {openId === r.id ? "Hide" : "View"}
                    </button>

                    <button
                      className="px-2 py-1 rounded-md border border-gray-300 hover:bg-gray-50 text-xs sm:text-sm"
                      onClick={async () => {
                        if (view === VIEW.CYL) await pdfOneCyl(r, logoImg);
                        else await pdfOneParts(r, logoImg);
                      }}
                      title="Generate PDF"
                    >
                      PDF
                    </button>

                    <button
                      className="px-2 py-1 rounded-md border border-amber-300 text-amber-700 hover:bg-amber-50 text-xs sm:text-sm"
                      onClick={() => demoteToDraft(r, true)}
                      title="Demote to draft and open"
                      disabled={loading}
                    >
                      Demote & open
                    </button>

                    <button
                      className="px-2 py-1 rounded-md border border-gray-300 hover:bg-gray-50 text-xs sm:text-sm"
                      onClick={() => archiveReport(r)}
                      title="Mark complete & move to archive"
                      disabled={loading}
                    >
                      Archive
                    </button>
                  </div>
                </div>

                {openId === r.id && (
                  <div className="p-4 bg-gray-50 border-t text-sm leading-relaxed overflow-x-auto">
                    <ReportPanel r={r} view={view} />
                    <ImagesPanel r={r} />
                    <AdminBox r={r} onSave={(payload) => saveAdmin(r.id, payload)} />
                  </div>
                )}
              </div>
              </div>
          );
        })}
      </div>

      {/* pagination */}
      <div className="mt-4 flex items-center gap-2">
        <button
          className="px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50"
          disabled={loading || pageIndex === 0}
          onClick={prevPage}
        >
          Prev
        </button>
        <span className="text-sm text-gray-600">Page {pageIndex + 1}</span>
        <button
          className="px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50"
          disabled={loading || rows.length < PAGE_SIZE}
          onClick={nextPage}
        >
          Next
        </button>
      </div>
    </div>
  );
}

/* ---------- helpers ---------- */

function KV({ label, value, mono }) {
  return (
    <div className="flex gap-2">
      <span className="w-48 text-gray-600">{label}</span>
      <span className={mono ? "font-mono" : ""}>{value || "—"}</span>
    </div>
  );
}

function partsMode(p) {
  if (p?.noPartsReturned) return "No parts returned";
  if (p?.allPartsReturned) return "All parts returned";
  if (p?.partialReturn) {
    const items = Object.entries(p.partialReturnItems || {})
      .filter(([, v]) => !!v)
      .map(([k]) => labelize(k));
    return items.length ? `Partial: ${items.join(", ")}` : "Partial: none selected";
  }
  return "—";
}

function labelize(k = "") {
  return k.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase());
}

// tiny heuristic for progress bar when demoting newly
function estimateProgress(view, payload = {}) {
  const f = payload.form || {};
  let s = 0;
  if (f.returnsNoteNumber) s += 12;
  if (f.serialNumber) s += 12;
  if (view === "cylinders") {
    if (f.cylinderModel) s += 12;
  } else {
    if (f.partCode) s += 12;
  }
  return Math.min(100, s);
}

function statusClasses(status) {
  switch (status) {
    case "new":
      return "bg-blue-50 text-blue-700 border-blue-200";
    case "waiting_collection":
    case "waiting_processing":
      return "bg-amber-50 text-amber-700 border-amber-200";
    case "in_progress":
      return "bg-indigo-50 text-indigo-700 border-indigo-200";
    case "complete":
      return "bg-emerald-50 text-emerald-700 border-emerald-200";
    case "straggler":
      return "bg-rose-50 text-rose-700 border-rose-200";
    default:
      return "bg-gray-50 text-gray-700 border-gray-200";
  }
}

function statusLabel(status) {
  if (!status) return "new";
  return String(status)
    .replace(/_/g, " ")
    .replace(/^\w/, (c) => c.toUpperCase());
}


/* ---------- Report panel ---------- */

function ReportPanel({ r, view }) {
  const f = r.payload?.form || {};
  const p = r.payload?.parts || {};
  const l = r.payload?.leaks || {};
  const c = r.payload?.cylCheck || {};

  if (view === "parts") {
    const t = r.payload?.templateSnapshot || {};
    const p = r.payload?.parts || {};
    return (
      <div className="space-y-3">
        <h3 className="font-semibold">Return & Part</h3>
        <KV label="Returns Note #" value={f.returnsNoteNumber} />
        <KV label="Serial" value={f.serialNumber} mono />
        <KV label="SRET #" value={f.sretNumber} mono />
        <KV label="Part Code" value={f.partCode} />
        <KV label="Part Title" value={f.partTitle || t.title} />
        <KV label="Part Description" value={f.partDescription || t.description} />

        <h3 className="font-semibold mt-4">Reported / Fault Found</h3>
        <KV label="Reported" value={f.reportedCode} />
        <KV label="Fault Found" value={f.faultFoundCode} />

        {p.notes && (
          <>
            <h3 className="font-semibold mt-4">Parts Notes</h3>
            <div className="text-sm whitespace-pre-wrap">{p.notes}</div>
          </>
        )}

        {f.internalNotes && (
          <>
            <h3 className="font-semibold mt-4">Engineer / Internal Notes</h3>
            <div className="text-sm whitespace-pre-wrap">
              {f.internalNotes}
            </div>
          </>
        )}

        {r.adminNotes && (
          <>
            <h3 className="font-semibold mt-4">Office / Admin Notes</h3>
            <div className="text-sm whitespace-pre-wrap text-red-600">
              {r.adminNotes}
            </div>
          </>
        )}
      </div>
    );
  }


  return (
    <div className="space-y-3">
      <h3 className="font-semibold">Return & Cylinder</h3>
      <KV label="Returns Note #" value={f.returnsNoteNumber} />
      <KV label="Serial" value={f.serialNumber} mono />
      <KV label="SRET #" value={f.sretNumber} mono />
      <KV label="Model" value={f.cylinderModel} />
      <KV label="Capacity" value={f.capacity} />
      <KV label="Manufacture Date" value={f.dateOfManufacture} />

      <h3 className="font-semibold mt-4">Reasons & Fault</h3>
      <KV label="Return Reason" value={f.reasonCode} />
      <KV label="Reason Defect" value={f.reasonDefectCode} />
      <KV label="Fault Found" value={f.faultFoundCode} />
      <KV label="Fault Defect" value={f.faultDefectCode} />

      <h3 className="font-semibold mt-4">Cylinder Checklist</h3>
      <KV label="Installed" value={bool(c.cylinderInstalled)} />
      <KV label="Damage: Case" value={bool(c.damageToCase)} />
      <KV label="Damage: Packaging" value={bool(c.damageToPackaging)} />
      <KV label="Damage: Bosses" value={bool(c.damageToBosses)} />
      <KV label="Strip & Test" value={bool(c.stripAndTest)} />

      <h3 className="font-semibold mt-4">Parts Mode</h3>
      <KV label="Mode" value={partsMode(p)} />

      {/* NEW: free-text notes visible on the view panel */}
      {p.notes && (
        <>
          <h3 className="font-semibold mt-4">Parts Notes</h3>
          <div className="text-sm whitespace-pre-wrap">{p.notes}</div>
        </>
      )}

      {f.internalNotes && (
        <>
          <h3 className="font-semibold mt-4">Engineer / Internal Notes</h3>
          <div className="text-sm whitespace-pre-wrap">
            {f.internalNotes}
          </div>
        </>
      )}

      {r.adminNotes && (
        <>
          <h3 className="font-semibold mt-4">Office / Admin Notes</h3>
          <div className="text-sm whitespace-pre-wrap text-red-600">
            {r.adminNotes}
          </div>
        </>
      )}
    </div>
  );

}

/* ---------- PDF helpers shared ---------- */

function bool(v) {
  return v ? "Yes" : "No";
}

function asLines(text, maxLen = 90) {
  if (!text) return [""];
  const words = String(text).split(/\s+/);
  const lines = [];
  let cur = "";
  for (const w of words) {
    if ((cur + " " + w).trim().length > maxLen) {
      lines.push(cur.trim());
      cur = w;
    } else {
      cur = (cur + " " + w).trim();
    }
  }
  if (cur) lines.push(cur);
  return lines;
}

function pdfReportFileName(r) {
  const f = r.payload?.form || {};
  const rawSret = f.sretNumber ? String(f.sretNumber).trim() : "";
  const rawRn = f.returnsNoteNumber ? String(f.returnsNoteNumber).trim() : "";

  const sretPart = rawSret
    ? (rawSret.toUpperCase().startsWith("SRET") ? rawSret : `SRET${rawSret}`)
    : "SRETUNKNOWN";

  const rnPart = rawRn || "RN-UNKNOWN";

  // strip characters illegal in filenames
  const base = `${sretPart} - ${rnPart}`.replace(/[\/\\:?*"<>|]/g, "_");
  return `${base}.pdf`;
}


function drawTable(doc, rows, x, y) {
  const body = rows.map((r) => r.map((c) => (c == null ? "—" : String(c))));
  autoTable(doc, {
    body,
    startY: y,
    theme: "grid",
    styles: { fontSize: 9, cellPadding: 4, halign: "left", valign: "middle" },
    headStyles: { fillColor: [240, 240, 240] },
    margin: { left: x, right: 40 },
  });
}

/* ---------- Images (reuses your SharePoint callables) ---------- */

export async function fetchDraftFolderAndImages(r) {
  const draftId = r.draftRef;
  if (!draftId) return { folderId: null, folderUrl: null, images: [] };

  const draftCol = r.payload?.form?.partCode ? "returns_parts_draft" : "returns_draft";
  const draftSnap = await getDoc(doc(db, draftCol, draftId));
  if (!draftSnap.exists()) return { folderId: null, folderUrl: null, images: [] };
  const storage = draftSnap.data()?.storage || {};
  const folderId = storage.folderId || null;
  const folderUrl = storage.folderUrl || null;
  if (!folderId) return { folderId: null, folderUrl: folderUrl || null, images: [] };

  const fns = getFunctions(undefined, "europe-west2");
  const list = httpsCallable(fns, "spListImages");
  const { data } = await list({ folderItemId: folderId });
  return { folderId, folderUrl, images: data?.images || [] };
}

async function getImageBase64(itemId) {
  const fns = getFunctions(undefined, "europe-west2");
  const getData = httpsCallable(fns, "spGetImageData");
  const { data } = await getData({ itemId });
  return data; // { mime, base64 }
}

/* ---------- PDF: Cylinders ---------- */

/* ---------- PDF: Cylinders ---------- */

export async function pdfOneCyl(r, logoImg) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const f = r.payload?.form || {};
  const p = r.payload?.parts || {};
  const l = r.payload?.leaks || {};
  const c = r.payload?.cylCheck || {};
  const dt = r.createdAt?.toDate?.();
  const pageW = doc.internal.pageSize.getWidth();

  // --- Header ---
  if (logoImg) {
    const naturalW = logoImg.naturalWidth || 64;
    const naturalH = logoImg.naturalHeight || 64;
    const w = LOGO_W_PT;
    const h = (naturalH / naturalW) * w;
    doc.addImage(logoImg, "PNG", pageW - 40 - w, 24, w, h);
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text("Cylinder Return Report", 40, 50);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  if (dt) doc.text(`Created: ${dt.toLocaleDateString()}`, 40, 68);
  if (r.status) doc.text(`Status: ${String(r.status)}`, 40, 82);

  // --- Return & Cylinder ---
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text("Return & Cylinder", 40, 110);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  drawTable(
    doc,
    [
      ["Returns Note #", f.returnsNoteNumber || "—", "Serial", f.serialNumber || "—"],
      ["SRET #", f.sretNumber || "—", "Model", f.cylinderModel || "—"],
      ["Capacity", f.capacity || "—", "Manufacture Date", f.dateOfManufacture || "—"],
    ],
    40,
    120
  );

  let y = (doc.lastAutoTable?.finalY ?? 200) + 20;

  // --- Reasons & Fault ---
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text("Reasons & Fault Found", 40, y);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  drawTable(
    doc,
    [
      ["Return Reason", f.reasonCode || "—", "Reason Defect", f.reasonDefectCode || "—"],
      ["Fault Found", f.faultFoundCode || "—", "Fault Defect", f.faultDefectCode || "—"],
    ],
    40,
    y + 10
  );

  // --- Cylinder Checklist ---
  y = (doc.lastAutoTable?.finalY ?? y) + 20;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text("Cylinder Checklist", 40, y);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  drawTable(
    doc,
    [
      ["Installed", bool(c.cylinderInstalled), "Damage to Case", bool(c.damageToCase)],
      ["Damage to Packaging", bool(c.damageToPackaging), "Damage to Bosses", bool(c.damageToBosses)],
      ["Strip & Test", bool(c.stripAndTest), "", ""],
    ],
    40,
    y + 10
  );

  // --- Leak Checklist (you lost this in the refactor) ---
  y = (doc.lastAutoTable?.finalY ?? y) + 20;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text("Leak Checklist", 40, y);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  drawTable(
    doc,
    [
      ["Primary", bool(l.primary), "Secondary", bool(l.secondary)],
      ["Connections", bool(l.connections), "Cylinder Body", bool(l.cylinderBody)],
      ["Other", bool(l.other), "Other Comments", l.other ? (l.otherComments || "—") : "—"],
    ],
    40,
    y + 10
  );

  // --- Parts (mode + notes + RMA) ---
  y = (doc.lastAutoTable?.finalY ?? y) + 20;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text("Parts", 40, y);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);

  // Rebuild mode text (same logic as partsMode, but inline so we can add notes)
  const items = Object.entries(p.partialReturnItems || {})
    .filter(([, v]) => !!v)
    .map(([k]) => labelize(k))
    .join(", ");

  const mode = p.noPartsReturned
    ? "No parts returned"
    : p.allPartsReturned
    ? "All parts returned"
    : p.partialReturn
    ? `Partial: ${items || "none selected"}`
    : "—";

  const partNotesLines = asLines(p.notes || "", 60);
  const partNotesCell =
    partNotesLines.length > 1
      ? [partNotesLines.join(" / "), ""]
      : [p.notes || "—", ""];

  drawTable(
    doc,
    [
      ["Mode", mode, "RMA Ref", p.rmaRef || "—"],
      ["Parts Notes", ...partNotesCell],
    ],
    40,
    y + 10
  );

  // --- Operators ---
  y = (doc.lastAutoTable?.finalY ?? y) + 20;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text("Operators", 40, y);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  drawTable(
    doc,
    [
      ["Welded", f.operators?.welded || "—", "Air Test", f.operators?.airTest || "—"],
      ["Hot Tested", f.operators?.hotTested || "—", "Tested", f.operators?.tested || "—"],
    ],
    40,
    y + 10
  );

  // --- Engineer Notes ---
  y = (doc.lastAutoTable?.finalY ?? y) + 20;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text("Engineer Notes", 40, y);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);

  const internalLines = asLines(f.internalNotes || "—", 100);
  let boxTop = y + 12;
  const boxLeft = PDF_LEFT_MARGIN;
  const boxRight = doc.internal.pageSize.getWidth() - 40;
  const lineHeight = 14;
  let boxHeight = Math.max(40, internalLines.length * lineHeight + 12);

  doc.roundedRect(boxLeft, boxTop, boxRight - boxLeft, boxHeight, 4, 4);
  let cy = boxTop + 16;
  internalLines.forEach((ln) => {
    doc.text(ln, boxLeft + 10, cy);
    cy += lineHeight;
  });

  // --- Office/Admin Notes ---
  y = boxTop + boxHeight + 20;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text("Office Notes (Internal)", 40, y);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);

  const adminLines = asLines(r.adminNotes || "—", 100);
  boxTop = y + 12;
  boxHeight = Math.max(40, adminLines.length * lineHeight + 12);

  // red box + text
  doc.setDrawColor(220, 0, 0);
  doc.setTextColor(220, 0, 0);

  doc.roundedRect(boxLeft, boxTop, boxRight - boxLeft, boxHeight, 4, 4);
  cy = boxTop + 16;
  adminLines.forEach((ln) => {
    doc.text(ln, boxLeft + 10, cy);
    cy += lineHeight;
  });

  // reset colours for anything after this
  doc.setDrawColor(0, 0, 0);
  doc.setTextColor(0, 0, 0);


  // --- Photos (with proper pagination) ---
  const { images } = await fetchDraftFolderAndImages(r);
  if (images && images.length) {
    const afterNotesY = boxTop + boxHeight + 24;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text("Photos", PDF_LEFT_MARGIN, afterNotesY);

    const gridStartY = afterNotesY + 8;

    const toUse = images.slice(0, PDF_MAX_IMAGES);
    const datas = [];

    for (const img of toUse) {
      try {
        const { mime, base64 } = await getImageBase64(img.id);
        if (mime && base64) {
          datas.push({ mime, base64, name: img.name });
        }
      } catch (e) {
        console.error("Image fetch failed", img.id, e);
      }
    }

    if (datas.length) {
      const pageH = doc.internal.pageSize.getHeight();
      let x = PDF_LEFT_MARGIN;
      let yPos = gridStartY;
      let col = 0;

      for (let i = 0; i < datas.length; i++) {
        // Page break BEFORE drawing if needed
        if (yPos + PDF_IMG_SIZE + 20 > pageH) {
          doc.addPage();
          x = PDF_LEFT_MARGIN;
          yPos = 40;
          col = 0;
        }

        const dtImg = datas[i];
        const fmt = dtImg.mime.toLowerCase().includes("png") ? "PNG" : "JPEG";
        const dataUrl = `data:${dtImg.mime};base64,${dtImg.base64}`;

        try {
          doc.addImage(
            dataUrl,
            fmt,
            x,
            yPos,
            PDF_IMG_SIZE,
            PDF_IMG_SIZE,
            undefined,
            "FAST"
          );
        } catch (e) {
          console.error("addImage failed", e);
        }

        // filename label under image
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        const label = (dtImg.name || "").slice(0, 30);
        doc.text(label, x, yPos + PDF_IMG_SIZE + 10, {
          maxWidth: PDF_IMG_SIZE,
        });

        col++;
        if (col >= PDF_IMG_COLS) {
          col = 0;
          x = PDF_LEFT_MARGIN;
          yPos = yPos + PDF_IMG_SIZE + 24;
        } else {
          x = x + PDF_IMG_SIZE + PDF_IMG_GAP;
        }
      }
    } else {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.text(
        "No images available to embed.",
        PDF_LEFT_MARGIN,
        gridStartY + 14
      );
    }
  }

  doc.save(pdfReportFileName(r));
}



/* ---------- PDF: Parts (lightweight) ---------- */

export async function pdfOneParts(r, logoImg) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const f = r.payload?.form || {};
  const t = r.payload?.templateSnapshot || {};
  const dt = r.createdAt?.toDate?.();
  const pageW = doc.internal.pageSize.getWidth();

  if (logoImg) {
    const naturalW = logoImg.naturalWidth || 64;
    const naturalH = logoImg.naturalHeight || 64;
    const w = LOGO_W_PT;
    const h = (naturalH / naturalW) * w;
    doc.addImage(logoImg, "PNG", pageW - 40 - w, 24, w, h);
  }
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text("Parts Return Report", 40, 50);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  if (dt) doc.text(`Created: ${dt.toLocaleDateString()}`, 40, 68);
  if (r.status) doc.text(`Status: ${String(r.status)}`, 40, 82);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text("Return & Part", 40, 110);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  drawTable(
    doc,
    [
      ["Returns Note #", f.returnsNoteNumber || "—", "Serial", f.serialNumber || "—"],
      ["SRET #", f.sretNumber || "—", "Part Code", f.partCode || "—"],
      ["Part Title", f.partTitle || t.title || "—", "Part Desc", (f.partDescription || t.description || "—").slice(0, 48)],
    ],
    40,
    120
  );

  let y = (doc.lastAutoTable?.finalY ?? 200) + 20;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text("Reported / Fault Found", 40, y);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  drawTable(
    doc,
    [
      ["Reported", f.reportedCode || "—", "Fault Found", f.faultFoundCode || "—"],
    ],
    40,
    y + 10
  );

  doc.save(pdfReportFileName(r));
}



/* ---------- Images panel + Admin box (kept from your original structure) ---------- */

export function ImagesPanel({ r }) {
  const [loading, setLoading] = useState(false);
  const [folderUrl, setFolderUrl] = useState(null);
  const [thumbs, setThumbs] = useState([]); // [{ id, name, thumbUrl, linkUrl }]

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      try {
        // Same helper as PDF
        const { folderUrl, images } = await fetchDraftFolderAndImages(r);

        if (cancelled) return;

        setFolderUrl(folderUrl || null);

        if (!images || !images.length) {
          setThumbs([]);
          return;
        }

        // Show a handful for performance
        const take = images.slice(0, 6);

        const withData = await Promise.all(
          take.map(async (img) => {
            try {
              const data = await getImageBase64(img.id);
              if (!data?.base64 || !data?.mime) return null;

              // Prefer any direct webUrl from SharePoint if present for the full-size link
              const linkUrl =
                img.webUrl || img.url || `data:${data.mime};base64,${data.base64}`;

              return {
                id: img.id,
                name: img.name,
                thumbUrl: `data:${data.mime};base64,${data.base64}`,
                linkUrl,
              };
            } catch (e) {
              console.error("Image thumb fetch failed", img.id, e);
              return null;
            }
          })
        );

        if (cancelled) return;

        setThumbs(withData.filter(Boolean));
      } catch (e) {
        console.error("ImagesPanel load failed", e);
        if (!cancelled) {
          setThumbs([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [r.id, r.draftRef]);

  if (!folderUrl && !thumbs.length && !loading) return null;

  return (
    <div className="mt-4">
      <div className="flex items-center justify-between mb-2">
        <span className="font-semibold text-sm">Photos</span>
        {folderUrl && (
          <a
            href={folderUrl}
            target="_blank"
            rel="noreferrer"
            className="text-xs text-indigo-600 hover:underline"
          >
            Open folder
          </a>
        )}
      </div>

      {loading && (
        <div className="text-xs text-gray-500">Loading images…</div>
      )}

      {!loading && thumbs.length === 0 && (
        <div className="text-xs text-gray-500">No images available.</div>
      )}

      {!loading && thumbs.length > 0 && (
        <div className="flex flex-wrap gap-3">
          {thumbs.map((img) => (
            <div
              key={img.id}
              className="w-60 h-60 bg-gray-100 rounded-md overflow-hidden border flex items-center justify-center"
            >
              <a
                href={img.linkUrl}
                target="_blank"
                rel="noreferrer"
                title={img.name}
                className="block w-full h-full"
              >
                <img
                  src={img.thumbUrl}
                  alt={img.name}
                  className="w-full h-full object-cover"
                />
              </a>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}



function AdminBox({ r, onSave }) {
  const [adminNotes, setAdminNotes] = useState(r.adminNotes || "");
  const [status, setStatus] = useState(r.status || "new");
  return (
    <div className="mt-4 p-3 bg-white border rounded-md">
      <div className="font-semibold mb-2">Admin</div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Status</label>
          <select
            className="text-sm rounded-md border border-gray-300 px-3 py-2 w-full"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            {/* allow any; archiving enforces "complete" anyway */}
            <option value="new">New</option>
            <option value="waiting_collection">Waiting for collection</option>
            <option value="waiting_processing">Waiting to be processed</option>
            <option value="in_progress">In progress</option>
            <option value="complete">Complete</option>
            <option value="straggler">Straggler</option>
          </select>
        </div>
        <div className="sm:col-span-2">
          <label className="block text-xs text-gray-500 mb-1">Admin Notes</label>
            <textarea
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              rows={3}
            />
          </div>
        </div>
      <div className="mt-3">
        <button
          className="px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50 text-sm"
          onClick={() => onSave({ adminNotes, status })}
        >
          Save admin
        </button>
      </div>
    </div>
  );
}
